import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Long text cut to a few lines with "See more" / "See less". The toggle only
 * appears when the text is actually longer than the limit.
 */
const ExpandableText: React.FC<{
  text?: string | null;
  lines?: number;
  className?: string;
  empty?: string;
}> = ({ text, lines = 4, className = "text-gray-600", empty }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, lines, expanded]);

  if (!text) return <p className={className}>{empty ?? t("na")}</p>;

  return (
    <div>
      <p
        ref={ref}
        className={`${className} whitespace-pre-line break-words`}
        style={
          expanded
            ? undefined
            : {
                display: "-webkit-box",
                WebkitLineClamp: lines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
        }
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-1 text-sm font-medium text-red-600 hover:underline"
        >
          {expanded
            ? t("see_less", { defaultValue: "See less" })
            : t("see_more", { defaultValue: "See more" })}
        </button>
      )}
    </div>
  );
};

export default ExpandableText;
